import OrganizationDutySettings from "./OrganizationDutySettings";
import OrganizationDutyHistory from "./OrganizationDutyHistory";
import OrganizationLiveToggle from "./OrganizationLiveToggle";
import UserDirectory from "../users/UserDirectory";
import OrganizationConnections from "./OrganizationConnections";
import BlockerReasonsEditor from "../settings/BlockerReasonsEditor";
import OrganizationCustomers from "../crm/OrganizationCustomers";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { api } from "../../lib/api";
import type { OrganizationDetailsResponse, OrganizationStatus } from "../../lib/types";
import { formatDateTime, labelFromCode } from "../shared/format";
import { RouteError, RouteLoading } from "../shared/PageState";
import OrganizationSettingsEditor from "./OrganizationSettingsEditor";
import OrganizationTypeField from "./OrganizationTypeField";
import OrganizationAddresses from "./OrganizationAddresses";
import OrganizationPocs from "./OrganizationPocs";
import OrganizationProjectPoc from "./OrganizationProjectPoc";
import OrganizationReporting from "./reporting/OrganizationReporting";

function OrganizationForm({ organization, canEdit, canApprove }: { organization: OrganizationDetailsResponse; canEdit: boolean; canApprove: boolean }) {
  const client = useQueryClient();
  const [name, setName] = useState(organization.organization_name);
  const [type, setType] = useState(organization.organization_type);
  const [status, setStatus] = useState(organization.status);
  const [gstin, setGstin] = useState(organization.gstin ?? "");
  const mutation = useMutation({
    mutationFn: () => api.updateAdminOrganization(organization.organization_id, {
      name: name.trim(),
      status,
      gstin: gstin.trim(),
      ...(type !== organization.organization_type ? { organization_type: type } : {})
    }),
    onSuccess: async () => { await Promise.all(["tech-ops-organization", "tech-ops-organizations", "tech-admin-workspace", "tech-admin-audit", "tech-admin-organization-settings", "tech-ops-dashboard"].map(key => client.invalidateQueries({ queryKey: [key] }))); }
  });
  return <form className="pa-panel pa-admin-form pa-detail-form" onSubmit={e => { e.preventDefault(); mutation.mutate(); }} onChange={() => mutation.reset()}>
    <div className="pa-detail-field-grid">
      <div><strong>{organization.is_live ? "Live organization" : "Awaiting Coprint approval"}</strong>
        <p className="pa-form-note">Review the profile and addresses before enabling Live. Workspace access requires a live organization.</p></div>
      <OrganizationLiveToggle organizationId={organization.organization_id} name={organization.organization_name} live={organization.is_live} canApprove={canApprove} />
      <div><span>Applicant type</span><p>{labelFromCode(organization.entity_type)}</p></div>
      <div><span>Contact email</span><p>{organization.email || "Not provided"}</p></div>
    </div>
    <fieldset className="pa-detail-fields" disabled={!canEdit || mutation.isPending}><legend>Organization details</legend><div className="pa-detail-field-grid">
      <label><span>Organization name</span><input required maxLength={150} value={name} onChange={e => setName(e.target.value)} /></label>
      <OrganizationTypeField value={type} onChange={value => { if (value) setType(value); }} />
      <label><span>Status</span><select value={status} onChange={e => setStatus(e.target.value as OrganizationStatus)}>{["ACTIVE", "SUSPENDED", "DISABLED"].map(s => <option key={s} value={s}>{labelFromCode(s)}</option>)}</select></label>
      <label><span>GSTIN</span><input value={gstin} onChange={e => setGstin(e.target.value.toUpperCase())} maxLength={15}
        pattern="[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]" title="Enter a valid 15-character GSTIN." placeholder="Enter GSTIN" /></label>
    </div>
    {type !== organization.organization_type && <p className="pa-form-note">Changing type updates the workflows and settings available to this organization. Existing records are retained.</p>}
    </fieldset>
    {mutation.isError && <p className="pa-form-error" role="alert">{mutation.error instanceof Error ? mutation.error.message : "Could not save organization."}</p>}
    {mutation.isSuccess && <p className="pa-form-success" role="status">Organization details saved.</p>}
    {canEdit ? <div className="pa-detail-actions"><button className="pa-secondary-button" type="button" disabled={mutation.isPending} onClick={() => { setName(organization.organization_name); setType(organization.organization_type); setStatus(organization.status); setGstin(organization.gstin ?? ""); mutation.reset(); }}>Reset changes</button><button className="pa-primary-button" type="submit" disabled={mutation.isPending}><Save size={16} />{mutation.isPending ? "Saving…" : "Save organization"}</button></div> : <p className="pa-form-note">Tech Admin access is required to edit organizations.</p>}
  </form>;
}

const sections = [
  { id: "profile", label: "Profile" },
  { id: "users", label: "Users" },
  { id: "customers", label: "Customers & vendors" },
  { id: "connections", label: "Connections" },
  { id: "addresses", label: "Addresses" },
  { id: "poc", label: "POC" },
  { id: "reporting", label: "Reporting" },
  { id: "duty", label: "Duty" },
  { id: "settings", label: "Settings" }
] as const;

// Mount on the first visit, then retain drafts when navigating between sections.
function OrganizationSection({ active, label, children }: { active: boolean; label: string; children: ReactNode }) {
  const [visited, setVisited] = useState(active);
  if (active && !visited) setVisited(true);
  return active || visited ? <section hidden={!active} aria-label={label}>{children}</section> : null;
}

export default function OrganizationDetailsPage() {
  const { organizationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const activeSection = sections.find(section => section.id === searchParams.get("tab"))?.id ?? "profile";
  const query = useQuery({ queryKey: ["tech-ops-organization", organizationId], queryFn: () => api.getOrganization(organizationId) });
  const dashboard = useQuery({ queryKey: ["tech-ops-dashboard"], queryFn: api.getDashboard });
  const sectionNav = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = sectionNav.current;
    const selected = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !selected) return;
    const container = nav.getBoundingClientRect(), item = selected.getBoundingClientRect();
    if (item.right > container.right) nav.scrollLeft += item.right - container.right;
    else if (item.left < container.left) nav.scrollLeft -= container.left - item.left;
  }, [activeSection, query.data?.organization_id]);
  if (query.isPending) return <RouteLoading />;
  if (query.isError || !query.data) return <RouteError title="Organization unavailable" detail={query.error instanceof Error ? query.error.message : "Could not load this organization."} onRetry={() => query.refetch()} />;
  const organization = query.data;
  const canEdit = dashboard.data?.platform_user.roles.includes("TECH_ADMIN") ?? false;
  const canApprove = dashboard.data?.platform_user.roles.some(role => role === "TECH_OPS" || role === "TECH_ADMIN") ?? false;
  return <div className="pa-page-stack pa-details-page">
    <Link className="pa-back-link" to="/organizations"><ArrowLeft size={16} /> Organizations</Link>
    <div className="pa-title-row"><div><h1>{organization.organization_name}</h1><p>Manage organization details and workflow settings.</p></div><span className="pa-badge" aria-label="Organization type">{organization.organization_type === "SMB" ? "SMB" : labelFromCode(organization.organization_type)}</span></div>
    <nav ref={sectionNav} className="pa-tabs pa-org-tabs" aria-label="Organization sections">
      {sections.map(section => {
        const params = new URLSearchParams(searchParams);
        params.set("tab", section.id);
        return <Link key={section.id} to={{ search: params.toString() }} aria-current={activeSection === section.id ? "page" : undefined}>{section.label}</Link>;
      })}
    </nav>
    <OrganizationSection key={`profile-${organizationId}`} label="Profile" active={activeSection === "profile"}>
      <div className="pa-page-stack">
        <OrganizationForm organization={organization} canEdit={canEdit} canApprove={canApprove} />
        <dl className="pa-record-meta"><div><dt>Organization ID</dt><dd>{organizationId}</dd></div><div><dt>Created</dt><dd>{formatDateTime(organization.created_at)}</dd></div><div><dt>Updated</dt><dd>{formatDateTime(organization.updated_at)}</dd></div></dl>
      </div>
    </OrganizationSection>
    <OrganizationSection key={`users-${organizationId}`} label="Users" active={activeSection === "users"}>
      <UserDirectory organization={organization} canAddUser={canEdit && organization.status === "ACTIVE"} active={activeSection === "users"} />
    </OrganizationSection>
    <OrganizationSection key={`customers-${organizationId}`} label="Customers & vendors" active={activeSection === "customers"}>
      <OrganizationCustomers organizationId={organizationId} canEdit={canEdit} />
    </OrganizationSection>
    <OrganizationSection key={`connections-${organizationId}`} label="Connections" active={activeSection === "connections"}>
      <OrganizationConnections organizationId={organizationId} canEdit={canEdit} organizationActive={organization.status === "ACTIVE"} active={activeSection === "connections"} />
    </OrganizationSection>
    <OrganizationSection key={`addresses-${organizationId}`} label="Addresses" active={activeSection === "addresses"}>
      <OrganizationAddresses organizationId={organizationId} isPrinter={organization.organization_type === "PRINTER"} canEdit={canEdit} active={activeSection === "addresses"} />
    </OrganizationSection>
    <OrganizationSection key={`poc-${organizationId}`} label="POC" active={activeSection === "poc"}>
      {organization.organization_type === "BRAND_OWNER"
        ? <OrganizationPocs organizationId={organizationId} canEdit={canEdit} />
        : <OrganizationProjectPoc organizationId={organizationId} canEdit={canEdit} />}
    </OrganizationSection>
    <OrganizationSection key={`settings-${organizationId}`} label="Settings" active={activeSection === "settings"}>
      {canApprove ? <OrganizationDutySettings organizationId={organizationId} /> : null}
      <div className="pa-page-stack">
      <div className="pa-panel pa-detail-padding pa-org-settings-panel">
        {canEdit ? <OrganizationSettingsEditor organizationId={organizationId} organizationType={organization.organization_type} />
          : <p className="pa-form-note">Tech Admin access is required to manage organization settings.</p>}
      </div>
      {canEdit ? <BlockerReasonsEditor organizationId={organizationId} /> : null}
      </div>
    </OrganizationSection>
    <OrganizationSection key={`reporting-${organizationId}`} label="Reporting" active={activeSection === "reporting"}>
      <OrganizationReporting organizationId={organizationId} active={activeSection === "reporting"} />
    </OrganizationSection>
    <OrganizationSection key={`duty-${organizationId}`} label="Duty" active={activeSection === "duty"}>
      <OrganizationDutyHistory organizationId={organizationId} active={activeSection === "duty"} />
    </OrganizationSection>
  </div>;
}
